'use client'

import { useState } from 'react'

import { useMutation } from '@apollo/client'
import { Button } from '@canyon/ui/Button'
import { useToast } from '@canyon/ui/useToast'
import { sendGTMEvent } from '@next/third-parties/google'
import { useSearchParams } from 'next/navigation'
import { useRouter } from 'next/navigation'

import { CredentialsAuthenticationSignUpDocument } from '@gql/graphql'

import { EmailSignUpForm } from '@/forms/EmailSignUpForm'
import { useEmailAuthSignUpForm } from '@/forms/hooks/useEmailAuthSignUpForm'
import { trackMetaConversion } from '@/utils/apiClient'

export const SignUpForm = () => {
  const [isSignUpLoading, setIsSignUpLoading] = useState(false)
  const { form } = useEmailAuthSignUpForm()
  const searchParams = useSearchParams()
  const router = useRouter()
  const { errorToast } = useToast()

  const [signUp] = useMutation(CredentialsAuthenticationSignUpDocument)

  const getCookie = (name: string) => {
    const value = `; ${document.cookie}`
    const parts = value.split(`; ${name}=`)
    if (parts.length === 2) {
      return parts.pop()?.split(';')?.shift()
    }
    return null
  }

  const handleSubmit = async () => {
    setIsSignUpLoading(true)
    const formValues = form.getValues()

    try {
      const { data } = await signUp({
        variables: {
          email: formValues.email,
          resumeNameSlug: getCookie('resumeExampleNameSlug'),
          utmSource: getCookie('utm_source'),
        },
      })

      if (data?.authSignUp) {
        const eventId = `signup_${formValues.email}_${Date.now()}`
        const userData = { em: formValues.email }

        // Get fbclid parameter from URL if available
        const fbclid = searchParams?.get('fbclid')

        // Server-side tracking via Meta Conversion API
        trackMetaConversion('CompleteRegistration', userData, eventId, {
          browser_id: 'email-signup',
          click_id: fbclid || undefined,
          external_id: formValues.email,
        }).catch((err) => console.error('Meta Conversion API error:', err))

        // Client-side Google conversion tracking via GTM
        sendGTMEvent({
          event: 'conversion',
        })

        router.push('/auth/verify-email-pending')
      }
    } catch (error) {
      const { networkError } = error

      if (networkError && networkError.result && networkError.result.errors?.length) {
        if (networkError.result.errors[0]?.message === 'user_already_exists') {
          form.setError('email', {
            message: 'Email is already in use',
          })
        }
      } else {
        errorToast({
          description: 'Please try again later.',
          title: 'Something went wrong',
        })
      }
    } finally {
      setIsSignUpLoading(false)
    }
  }

  return (
    <div className="space-y-5">
      <EmailSignUpForm form={form} onSubmit={handleSubmit} />
      <Button
        form="email-sign-up-form"
        fullWidth
        loading={isSignUpLoading}
        size="lg"
        type="submit"
        variant="primary"
      >
        Continue
      </Button>
    </div>
  )
}
